/**
 * Header — barra superior fixa do AppShell.
 *
 * Spec §10 (Layout Global) e §18 (Renderização Baseada em Permissões —
 * "Admin global — layout diferenciado"):
 *
 *   • JWT:   [Logo] [TenantSwitcher ▾] ............. [🌙] [🔔] [Avatar ▾]
 *   • Basic: [Logo] ................................. [🌙]      [Avatar ▾]
 *
 * O `TenantSwitcher` e o `NotificationBell` são ocultos em modo basic — o
 * admin global é tenant-agnóstico e não recebe notificações de execuções.
 *
 * O `UserMenu` (avatar → dropdown com nome/email/logout) está implementado
 * inline neste arquivo. Em modo basic, o avatar mostra "AG" (Admin Global)
 * e o email exibido é literal "admin" — não há /me consultável.
 */
import { LogOut } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import logoUrl from '@/assets/logo.svg'
import { NotificationBell } from '@/components/common/NotificationBell'
import { TenantSwitcher } from '@/components/common/TenantSwitcher'
import { ThemeToggle } from '@/components/common/ThemeToggle'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useAuth } from '@/hooks/useAuth'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/store/auth.store'

// ---------------------------------------------------------------------------
// UserMenu — dropdown com avatar + nome/email + logout.
// ---------------------------------------------------------------------------

function getInitials(name: string | null | undefined, email: string): string {
  const source = (name && name.trim()) || email
  const parts = source
    .replace(/[@._-]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
  if (parts.length === 0) return '??'
  const first = parts[0]?.[0] ?? ''
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0] ?? '' : ''
  return (first + last).toUpperCase().slice(0, 2) || '??'
}

interface UserMenuProps {
  className?: string
}

function UserMenu({ className }: UserMenuProps): React.ReactElement {
  const { t } = useTranslation('common')
  const { logout } = useAuth()
  const mode = useAuthStore((s) => s.mode)
  const user = useAuthStore((s) => s.user)

  // Em modo basic não há /me — usamos placeholders fixos.
  const isBasic = mode === 'basic'
  const displayName = isBasic ? 'Admin Global' : user?.name ?? ''
  const displayEmail = isBasic ? 'admin' : user?.email ?? ''
  const initials = getInitials(displayName, displayEmail)

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          data-testid="header-user-menu-trigger"
          aria-label={displayName || 'Menu do usuário'}
          className={cn('relative h-9 w-9 rounded-full', className)}
        >
          <Avatar className="h-9 w-9">
            <AvatarFallback className="text-xs font-medium">
              {initials}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-60"
        data-testid="header-user-menu"
      >
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col gap-0.5">
            <span
              data-testid="header-user-menu-name"
              className="truncate text-sm font-medium"
            >
              {displayName || '—'}
            </span>
            <span
              data-testid="header-user-menu-email"
              className="truncate text-xs text-muted-foreground"
            >
              {displayEmail || '—'}
            </span>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          data-testid="header-user-menu-logout"
          onSelect={() => {
            // logout é async mas idempotente e nunca lança — fire-and-forget.
            void logout()
          }}
          className="cursor-pointer"
        >
          <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />
          <span>{t('navigation.logout')}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

export interface HeaderProps {
  className?: string
}

export function Header({ className }: HeaderProps): React.ReactElement {
  const { t } = useTranslation('common')
  const mode = useAuthStore((s) => s.mode)
  const isBasic = mode === 'basic'

  return (
    <header
      data-testid="app-header"
      data-auth-mode={mode ?? 'none'}
      className={cn(
        'flex h-14 shrink-0 items-center justify-between gap-3 border-b border-border bg-card px-4',
        className,
      )}
    >
      {/* Esquerda: logo + TenantSwitcher (jwt) */}
      <div className="flex items-center gap-3">
        <Link
          to={isBasic ? '/admin/organizations' : '/'}
          data-testid="header-logo"
          className="flex items-center gap-2 rounded-md px-1 py-1 transition-opacity hover:opacity-80"
          aria-label={t('app.name')}
        >
          <img src={logoUrl} alt="" className="h-7 w-7" aria-hidden="true" />
          <span className="hidden text-sm font-semibold tracking-tight sm:inline">
            {t('app.name')}
          </span>
        </Link>

        {!isBasic ? <TenantSwitcher /> : null}
      </div>

      {/* Direita: ThemeToggle + NotificationBell (jwt) + UserMenu */}
      <div className="flex items-center gap-1">
        <ThemeToggle />
        {!isBasic ? <NotificationBell /> : null}
        <UserMenu />
      </div>
    </header>
  )
}
